import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import { SolverStatus } from '../types';
import { Button } from '../components/ui/button';

export default function SolverDashboardPage() {
  const { user } = useAuthStore();
  const [solverStatus, setSolverStatus] = useState<SolverStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await api.get('/solver/status');
        setSolverStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch solver status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatus();
    
    // Set up polling for real-time updates
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleStartSolver = async () => {
    try {
      await api.post('/solver/start');
      setIsRunning(true);
    } catch (error) {
      console.error('Failed to start solver:', error);
    }
  };

  const handleStopSolver = async () => {
    try {
      await api.post('/solver/stop');
      setIsRunning(false);
    } catch (error) {
      console.error('Failed to stop solver:', error);
    }
  };

  const totalEarnings = solverStatus.reduce((sum, account) => sum + account.totalEarnings, 0);
  const activeAccounts = solverStatus.filter(account => account.status === 'active').length;

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">Loading...</div>;
  }

  return (
    <div className="container mx-auto p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Solver Dashboard</h1>
        <div className="space-x-4">
          {!isRunning ? (
            <Button onClick={handleStartSolver} className="bg-green-600 hover:bg-green-700">
              Start Solver
            </Button>
          ) : (
            <Button onClick={handleStopSolver} className="bg-red-600 hover:bg-red-700">
              Stop Solver
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-2">Total Earnings</h3>
          <p className="text-2xl font-bold text-green-400">${totalEarnings.toFixed(2)}</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-2">Active Accounts</h3>
          <p className="text-2xl font-bold text-blue-400">{activeAccounts} / {solverStatus.length}</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-2">Solver Status</h3>
          <p className="text-2xl font-bold text-yellow-400">{isRunning ? 'Running' : 'Stopped'}</p>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700">
          <h2 className="text-xl font-semibold">Account Status</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Platform</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Earnings</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Success Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Ban Risk</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {solverStatus.map((account) => (
                <tr key={account.accountId}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                    {account.platformName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      account.status === 'active' ? 'bg-green-800 text-green-100' : 'bg-gray-800 text-gray-100'
                    }`}>
                      {account.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    ${account.earnings.toFixed(2)} / ${account.totalEarnings.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {account.successRate}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    <span className={`${
                      account.banRisk > 70 ? 'text-red-400' : 
                      account.banRisk > 40 ? 'text-yellow-400' : 
                      'text-green-400'
                    }`}>
                      {account.banRisk}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
